import { FC, useContext, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { states } from "capitalrx-types";
import { Checkbox, Dropdown, FileEntry, RadioGroup, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Button, Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading, Subheading } from "capitalrx-components-2.0/lib/typography";
import { validate } from "capitalrx-components-2.0";

import { ProviderManagement } from "@/api/interfaces";
import { TypeForm, PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, validatePhoneNumber, formatPhoneWithExtension, formatZipCode } from "@/utils";

import { FieldsBankAccount } from "./FieldsBankAccount";
import { FieldsRemittanceType } from "./FieldsRemittanceType";
import { ProviderNetworkDropdown } from "./ProviderNetworkDropdown";
import { FieldsTransactionValidation } from "./FieldsTransactionValidation";
import { FieldsDocuments } from "./FieldsDocuments";


export function checkSameRemittanceAddress(
  provider: ProviderManagement.ProviderSchema,
  paymentPreference: ProviderManagement.PaymentPreferenceSchema,
) {
  if (
    provider.mailing_address_line_1
    && provider.mailing_address_line_1 === paymentPreference?.remittance_address_line_1
    && provider.mailing_address_line_2 === paymentPreference?.remittance_address_line_2
    && provider.mailing_address_city === paymentPreference?.remittance_address_city
    && provider.mailing_address_state === paymentPreference?.remittance_address_state
    && provider.mailing_address_zip_code === paymentPreference?.remittance_address_zip_code
  ) {
    return true;
  }
  return false;
}

export interface BaseAddress {
  readonly street_address: string;
  readonly address_line: string;
  readonly city: string;
  readonly state: string;
  readonly zip_code: string;
}

export interface ProviderPaymentRemittanceDataForm {
  readonly opt_out: boolean;

  // Payment & Remittance
  readonly payment_type: ProviderManagement.PaymentTypeValue;

  // //Financial Information
  readonly financial_institutions: BaseAddress & {
    readonly name: string;
    readonly first_name: string;
    readonly last_name: string;
    readonly phone_number: string;
    readonly fax_number: string;
  };

  // //Bank Account
  readonly type_account: ProviderManagement.AccountTypeValue;
  readonly routing_number: string;
  readonly bank_account_number: string;
  readonly account_owner_first_name: string;
  readonly account_owner_last_name: string;
  readonly decision: string;

  // //Remittance Recipient Targets
  readonly email_address: string;
  readonly sftp_delivery_targets: string;
  readonly remittance_type: ProviderManagement.RemittanceTypeValue,
  // // Remit To Address
  readonly remit: BaseAddress & {
    readonly check: boolean;
  };

  readonly provider_network_id: number;
  // //Documents
  readonly authorization_letter: {
    files: FileEntry[]
    notes: string
  };
  readonly bank_letterhead_voided_check: {
    type: string
    files: FileEntry[]
    notes: string
  };
  readonly transaction_validation_claim_1_number: string;
  readonly transaction_validation_claim_1_paid_amount: string;
  readonly transaction_validation_claim_2_number: string;
  readonly transaction_validation_claim_2_paid_amount: string;
}

export interface PaymentRemittanceGroupFieldsProps {
  readonly readOnly?: boolean;
  readonly provider: ProviderManagement.ProviderSchema;
  readonly paymentPreference: ProviderManagement.PaymentPreferenceSchema;
  readonly setPaymentPreference: Function;
  readonly documents: ProviderManagement.DocumentSchema[];
  readonly typeForm: TypeForm;
  readonly onEdit?: () => void;
}

export const PaymentRemittanceGroupFields: FC<PaymentRemittanceGroupFieldsProps> = ({ readOnly, provider, paymentPreference, setPaymentPreference, documents, typeForm, onEdit }) => {
  const { threeFull, sixFull, twelve } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { primary_color } = useContext(ThemeSelectorContext);
  const { control, setValue } = useFormContext();
  const [paymentType, optOut] = useWatch({
    control,
    name: ["payment_type", "opt_out"],
  });

  // Watch phone/fax fields to normalize formatting while typing
  const [fiPhone, fiFax] = useWatch({
    control,
    name: ["financial_institutions.phone_number", "financial_institutions.fax_number"],
  });

  /* Format phone/fax: insert dashes and 'x' for ext
     Only set when value changes to avoid update loops */
  useEffect(() => {
    if (fiPhone !== undefined) {
      const f = formatPhoneWithExtension(fiPhone);
      if (f !== fiPhone) {
        setValue("financial_institutions.phone_number", f, { shouldValidate: false });
      }
    }
    if (fiFax !== undefined) {
      const f = formatPhoneWithExtension(fiFax);
      if (f !== fiFax) {
        setValue("financial_institutions.fax_number", f, { shouldValidate: false });
      }
    }
  }, [fiPhone, fiFax, setValue]);

  // Watch zip code fields to normalize formatting while typing
  const [financialInstitutionZip] = useWatch({
    control,
    name: ["financial_institutions.zip_code"],
  });

  /* Format zip code: insert dash for ZIP+4
     Only set when value changes to avoid update loops */
  useEffect(() => {
    if (financialInstitutionZip !== undefined) {
      const f = formatZipCode(financialInstitutionZip);
      if (f !== financialInstitutionZip) {
        setValue("financial_institutions.zip_code", f, { shouldValidate: false });
      }
    }
  }, [financialInstitutionZip, setValue]);

  useEffect(() => {
    if (optOut) {
      setValue("provider_network_id", null);
      setPaymentPreference([]);
    }
  }, [optOut]);

  return (
    <Stack spacing="large">
      {typeForm !== "edit" && (
        <Stack>
          <Grid>
            <Shelf>
              <Heading>Network Selection</Heading>
              <Shelf.Spacer />
              {readOnly && onEdit && (
                <Button
                  icon="edit"
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={onEdit}
                >
                  Edit
                </Button>
              )}
            </Shelf>

            <Grid.Span columns={twelve}>
              {!paymentPreference?.provider_network_id && <Checkbox.Field
                label="Opt out of network enrollment"
                name="opt_out"
                hideLabel
                testId="test__opt_out"
                readOnly={readOnly}
              />}
            </Grid.Span>

            {!optOut && <Grid.Span columns={sixFull}>
              <ProviderNetworkDropdown name="provider_network_id" required readOnly={paymentPreference?.provider_network_id != null} />
            </Grid.Span>}
          </Grid>
        </Stack>
      )}
      {!optOut && <Stack>
        <Heading>Payment & Remittance</Heading>
        <Stack>
          <Subheading>Payment Type</Subheading>
          <Grid>
            <Grid.Span columns={twelve}>
              <RadioGroup.Field name="payment_type" testId="payment_type" orientation="horizontal" hideLabel readOnly={readOnly}>
                <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.ACH}>Bank Account/ACH</RadioGroup.Option>
                <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>Check</RadioGroup.Option>
              </RadioGroup.Field>
            </Grid.Span>
            {/* This next portion is commented as per ad-hoc design detailed in https://capitalrx.atlassian.net/browse/CAP-80855 since we'll need to bring it back for VCC */}
            {/* <Message appearance={Messages.Informational} visible={paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>Checks will be sent to the Provider’s mailing address</Message> */}
            {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK && <Message appearance={Messages.Informational} visible>{paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK ? ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.CHECK : ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.ACH}</Message>}
          </Grid>
        </Stack>
        {!readOnly
          && <Stack>
            {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH
              && <Stack>
                <Subheading>Financial Information</Subheading>
                <Grid>
                  <Grid.Span columns={twelve}>
                    <Textbox.Field
                      label="Financial Institution’s Name"
                      name="financial_institutions.name"
                      testId="financial_institutions_name"
                      required
                      readOnly={readOnly}
                    />
                  </Grid.Span>
                  <Grid.Break />
                  <Grid.Spread columns={twelve}>
                    <Textbox.Field
                      label="Street Address"
                      name="financial_institutions.street_address"
                      testId="financial_institutions_street_address"
                      required
                      readOnly={readOnly}
                    />
                    <Textbox.Field
                      label="Address Line 2"
                      name="financial_institutions.address_line"
                      testId="financial_institutions_address_line"
                      readOnly={readOnly}
                    />
                  </Grid.Spread>
                  <Grid.Span columns={sixFull}>
                    <Textbox.Field
                      label="City"
                      name="financial_institutions.city"
                      testId="financial_institutions_city"
                      required
                      readOnly={readOnly}
                    />
                  </Grid.Span>
                  <Grid.Spread columns={threeFull}>
                    <Dropdown.Field
                      label="State"
                      name="financial_institutions.state"
                      testId="financial_institutions_state"
                      required
                      searchable
                      readOnly={readOnly}
                    >
                      {Object.entries(states).map(([full, abbreviation]) => {
                        return (
                          <Dropdown.Option key={abbreviation} value={abbreviation}>
                            {full}
                          </Dropdown.Option>
                        );
                      })}
                    </Dropdown.Field>
                    <Textbox.Field
                      label="Zip Code"
                      name="financial_institutions.zip_code"
                      testId="financial_institutions_zip_code"
                      validator={(value) => validate.isZipCode(value)}
                      required
                      readOnly={readOnly}
                    />
                  </Grid.Spread>
                  <Grid.Spread columns={sixFull}>
                    <Textbox.Field
                      label="Contact First Name"
                      name="financial_institutions.first_name"
                      testId="financial_institutions_first_name"
                      required
                      readOnly={readOnly}
                    />
                    <Textbox.Field
                      label="Contact Last Name"
                      name="financial_institutions.last_name"
                      testId="financial_institutions_last_name"
                      required
                      readOnly={readOnly}
                    />
                    <Textbox.Field
                      label="Phone Number"
                      name="financial_institutions.phone_number"
                      testId="financial_institutions_phone_number"
                      instructions="123-456-7890 x 123"
                      validator={(value) => validatePhoneNumber(value)}
                      required
                      readOnly={readOnly}
                    />
                    <Textbox.Field
                      label="Fax Number"
                      name="financial_institutions.fax_number"
                      testId="financial_institutions_fax_number"
                      instructions="123-456-7890 x 123"
                      validator={(value) => validatePhoneNumber(value)}
                      required
                      readOnly={readOnly}
                    />
                  </Grid.Spread>
                </Grid>
              </Stack>
            }
            {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && <FieldsBankAccount readOnly={readOnly} typeForm={typeForm} />}
            <FieldsRemittanceType readOnly={readOnly} provider={provider} />
          </Stack>
        }
      </Stack>}
      {!optOut && provider?.type === ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL && (
        <Stack>
          <FieldsTransactionValidation readOnly={readOnly} />
        </Stack>
      )}
      {!optOut && paymentType !== ProviderManagement.PAYMENT_TYPE_VALUES.CHECK && <Stack>
        <Heading>Documents</Heading>
        <FieldsDocuments provider={provider} documents={documents} readOnly={readOnly} />
      </Stack>}
    </Stack>
  );
};
